/**
 * A data access agreement as HTML — for the in-app viewer and for the copy
 * printed for signature, so the two can never differ.
 *
 * The agreements mark three kinds of open item with pandoc bracketed spans
 * (`[text]{custom-style="Assumed"}`), which a markdown renderer prints
 * literally. They are rewritten to real spans BEFORE parsing — the same
 * order MessageBubble uses for citations, because marked's tokenizer would
 * otherwise split them — and the sanitiser keeps exactly those classes.
 */
import DOMPurify from "dompurify";
import { marked } from "marked";

const STYLE_CLASS: Record<string, string> = {
  Assumed: "daa-assumed",
  NeedsReview: "daa-review",
  NeedsData: "daa-data",
};

const SPAN_RE =
  /\[([^\]]*)\]\{custom-style="(Assumed|NeedsReview|NeedsData)"\}/g;

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

export function renderAgreementHtml(markdown: string): string {
  const marked_up = markdown.replace(
    SPAN_RE,
    (_m, text: string, style: string) =>
      `<span class="${STYLE_CLASS[style]}">${escapeHtml(text)}</span>`,
  );
  const raw = marked.parse(marked_up, { async: false, gfm: true }) as string;
  return DOMPurify.sanitize(raw, { ADD_ATTR: ["class"] });
}

const PRINT_CSS = `
  body { font: 11pt/1.45 Calibri, "Segoe UI", Arial, sans-serif; color: #111;
         max-width: 46rem; margin: 2rem auto; padding: 0 1rem; }
  h1 { font-size: 18pt; } h2 { font-size: 13pt; margin-top: 1.6em; }
  h3 { font-size: 11.5pt; }
  table { border-collapse: collapse; width: 100%; margin: .5em 0; }
  th, td { border: 1px solid #999; padding: .3em .5em; vertical-align: top;
           text-align: left; }
  blockquote { margin: .5em 0; padding: .2em .8em; border-left: 3px solid #bbb;
               color: #444; }
  .daa-assumed { color: #b45f06; font-style: italic; }
  .daa-review { color: #c00000; }
  .daa-data { color: #c00000; font-weight: 700; }
  h2 { break-after: avoid; } table, tr { break-inside: avoid; }
  @page { margin: 18mm; }
`;

/**
 * Open a print-ready copy in a new window. Call `openPrintWindow()` in the
 * click handler itself — a window opened after an `await` is a popup the
 * browser blocks — then `fillPrintWindow()` once the text has loaded.
 */
export function openPrintWindow(): Window | null {
  const w = window.open("", "_blank");
  if (w) {
    w.document.write(
      "<!doctype html><title>Preparing…</title><p>Preparing…</p>",
    );
  }
  return w;
}

export function fillPrintWindow(
  w: Window,
  title: string,
  markdown: string,
): void {
  const doc = w.document;
  doc.open();
  doc.write(
    `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>` +
      `<style>${PRINT_CSS}</style></head><body>${renderAgreementHtml(markdown)}</body></html>`,
  );
  doc.close();
  w.focus();
  // Give the browser a frame to lay the document out before the dialog.
  w.setTimeout(() => w.print(), 250);
}
